import { useEffect, useMemo, useRef, useState } from "react";

import {
  advanceState,
  classifySector,
  createRaceState,
  formatDelta,
  formatMs,
  formatSector,
  type LapRecord,
  type RaceState,
} from "./race-engine";
import {
  createViewState,
  fitProjection,
  interpPos,
  renderRace,
  type ProjState,
  type ViewState,
} from "./race-canvas";
import type { RaceDataset } from "./race-types";
import type { RacingPilotBestOut } from "@/api/racing";

interface LiveRaceStageProps {
  dataset: RaceDataset;
  currentT: number;
  statusLabel: string;
  statusMode: "live" | "inactive" | "replay";
  pilotBest?: RacingPilotBestOut | null;
}

export function LiveRaceStage({ dataset, currentT, statusLabel, statusMode, pilotBest }: LiveRaceStageProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const projRef = useRef<ProjState | null>(null);
  const viewRef = useRef<ViewState>(createViewState());
  const [, setRenderTick] = useState(0);

  const nGates = dataset.gates.length || 1;

  const raceStateRef = useRef<RaceState>(createRaceState(nGates));

  // Reset race state whenever the track topology or session changes.
  useEffect(() => {
    raceStateRef.current = createRaceState(nGates);
  }, [nGates, dataset.meta.session_key]);

  // Advance passes as currentT grows — then force a render tick.
  useEffect(() => {
    advanceState(raceStateRef.current, dataset.passes, currentT);
    setRenderTick((n) => n + 1);
  }, [dataset.passes, currentT]);

  // Fit + draw whenever the dataset or currentT changes.
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const rect = container.getBoundingClientRect();
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    canvas.width = Math.floor(rect.width * dpr);
    canvas.height = Math.floor(rect.height * dpr);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    const proj = fitProjection(dataset, rect);
    projRef.current = proj;
    renderRace(ctx, dataset, raceStateRef.current, currentT, proj, viewRef.current);
  }, [dataset, currentT]);

  // Redraw on resize.
  useEffect(() => {
    const handle = () => setRenderTick((n) => n + 1);
    window.addEventListener("resize", handle);
    return () => window.removeEventListener("resize", handle);
  }, []);

  const race = raceStateRef.current;
  const telemetry = useMemo(() => interpPos(dataset.trajectory, currentT), [dataset.trajectory, currentT]);

  const currentLapTime =
    race.currentLap > 0 && race.currentLapStartT !== null
      ? currentT - race.currentLapStartT
      : 0;

  const dbBestLapMs =
    pilotBest?.best_lap_seconds != null ? pilotBest.best_lap_seconds * 1000 : null;
  const dbBestSectorMsByIdx = useMemo(() => {
    const map = new Map<number, number>();
    for (const s of pilotBest?.best_sectors ?? []) {
      map.set(s.sector_index, s.duration_seconds * 1000);
    }
    return map;
  }, [pilotBest]);
  const lookupDbBestSectorMs = (sectorIdx: number): number | null =>
    dbBestSectorMsByIdx.get(sectorIdx) ?? null;

  const lastLapDelta =
    race.lastLapMs !== null && dbBestLapMs !== null
      ? race.lastLapMs - dbBestLapMs
      : null;
  const currentLapProjectedDelta =
    race.currentLap > 0 && dbBestLapMs !== null
      ? currentLapTime - dbBestLapMs
      : null;

  return (
    <div className="lr-layout">
      <section className="lr-panel lr-map-panel">
        <div className="lr-panel-head">
          <div className="lr-panel-head-left">
            <span className="lr-panel-title">TACTICAL MAP</span>
            <span className="lr-panel-badge lr-mono">
              {Math.round(dataset.bbox.max_x - dataset.bbox.min_x)}×
              {Math.round(dataset.bbox.max_y - dataset.bbox.min_y)}M
            </span>
          </div>
          <div className="lr-panel-head-right">
            <span className="lr-legend-item">
              <span className="lr-legend-swatch lr-sw-gate" />
              GATE
            </span>
            <span className="lr-legend-item">
              <span className="lr-legend-swatch lr-sw-path" />
              TRAJECTORY
            </span>
            <span className="lr-legend-item">
              <span className="lr-legend-swatch lr-sw-drone" />
              DRONE
            </span>
          </div>
        </div>
        <div className="lr-map-container" ref={containerRef}>
          <canvas ref={canvasRef} />
          <div className="lr-hud-corner lr-tl" />
          <div className="lr-hud-corner lr-tr" />
          <div className="lr-hud-corner lr-bl" />
          <div className="lr-hud-corner lr-br" />
        </div>
        <div className="lr-map-footer">
          <div className="lr-telemetry">
            <div className="lr-tl-cell">
              <span className="lr-tl-label">SPD</span>
              <span className="lr-tl-value lr-mono">
                {telemetry ? Math.round(telemetry.speed_ms * 3.6) : 0}
              </span>
              <span className="lr-tl-unit">km/h</span>
            </div>
            <div className="lr-tl-cell">
              <span className="lr-tl-label">ALT</span>
              <span className="lr-tl-value lr-mono">
                {telemetry ? Math.round(telemetry.alt) : 0}
              </span>
              <span className="lr-tl-unit">m</span>
            </div>
            <div className="lr-tl-cell">
              <span className="lr-tl-label">HDG</span>
              <span className="lr-tl-value lr-mono">
                {telemetry ? Math.round(((telemetry.yaw % 360) + 360) % 360) : 0}
              </span>
              <span className="lr-tl-unit">°</span>
            </div>
            <div className="lr-tl-cell">
              <span className="lr-tl-label">RUN T</span>
              <span className="lr-tl-value lr-mono">{formatMs(currentT)}</span>
            </div>
          </div>
          <div className={`lr-status-pill lr-status-${statusMode}`}>
            <span className="lr-status-dot" />
            <span className="lr-status-text">{statusLabel}</span>
          </div>
        </div>
      </section>

      <aside className="lr-panel lr-board-panel">
        <div className="lr-panel-head">
          <div className="lr-panel-head-left">
            <span className="lr-panel-title">TIMING TOWER</span>
            <span className="lr-panel-badge lr-mono">LAP {race.currentLap || 0}</span>
          </div>
          <div className="lr-panel-head-right">
            <span className="lr-legend-item">
              <span className="lr-legend-swatch lr-sw-purple" />
              OVERALL
            </span>
            <span className="lr-legend-item">
              <span className="lr-legend-swatch lr-sw-green" />
              PB
            </span>
            <span className="lr-legend-item">
              <span className="lr-legend-swatch lr-sw-yellow" />
              OFF-PB
            </span>
          </div>
        </div>

        <div className="lr-best-row">
          <div className="lr-best-box lr-best-overall">
            <div className="lr-best-label">BEST LAP</div>
            <div className="lr-best-time lr-mono">{formatMs(race.bestLapMs)}</div>
            <div className="lr-best-sub lr-mono">
              {race.bestLapNum != null ? `LAP ${race.bestLapNum}` : "—"}
              {dbBestLapMs !== null
                ? ` · DB ${formatMs(dbBestLapMs)}`
                : ""}
            </div>
          </div>
          <div className="lr-best-box lr-best-last">
            <div className="lr-best-label">LAST LAP</div>
            <div className="lr-best-time lr-mono">{formatMs(race.lastLapMs)}</div>
            <div className="lr-best-sub lr-mono">
              Δ PB{" "}
              {lastLapDelta !== null ? formatDelta(lastLapDelta) : "—"}
            </div>
          </div>
          <div className="lr-best-box lr-best-current">
            <div className="lr-best-label">CURRENT LAP</div>
            <div className="lr-best-time lr-mono">
              {race.currentLap > 0 ? formatMs(currentLapTime) : "--:--.---"}
            </div>
            <div className="lr-best-sub lr-mono">
              Δ PB{" "}
              {currentLapProjectedDelta !== null
                ? formatDelta(currentLapProjectedDelta)
                : "—"}
            </div>
          </div>
        </div>

        <div className="lr-sector-section">
          <div className="lr-sector-section-head">
            <span className="lr-section-title">CURRENT LAP SECTORS</span>
            <span className="lr-section-sub lr-mono">
              {race.currentLap > 0 ? formatMs(currentLapTime) : "00:00.000"}
            </span>
          </div>
          <div className="lr-sector-grid">
            {Array.from({ length: nGates }, (_, i) => i + 1).map((i) => {
              const sec = race.currentLapSectors[i - 1];
              const wasPB = race.currentLapSectorWasPB[i - 1];
              const active =
                race.lastPass !== null &&
                race.lastPass.gate_idx === i &&
                race.currentLap > 0 &&
                (sec === null || sec === undefined);
              const cls =
                sec != null
                  ? classifySector(race, race.currentLap, i, sec, wasPB, lookupDbBestSectorMs(i)) ?? "done"
                  : active
                    ? "active"
                    : "pending";
              const toG = (i % nGates) + 1;
              const label = sec != null
                ? formatSector(sec)
                : active && race.lastPass
                  ? formatSector(currentT - race.lastPass.t)
                  : "--.---";
              return (
                <div key={i} className={`lr-sector-cell lr-${cls}`}>
                  <div className="lr-s-head">
                    <span className="lr-s-name">S{i}</span>
                    <span className="lr-s-gates">
                      G{i}→G{toG}
                    </span>
                  </div>
                  <span className="lr-s-time">{label}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="lr-laps-section">
          <div className="lr-section-head">
            <span className="lr-section-title">LAP HISTORY</span>
            <span className="lr-section-sub lr-mono">
              {race.lapsCompleted.length} LAP
              {race.lapsCompleted.length === 1 ? "" : "S"}
            </span>
          </div>
          <div className="lr-laps-table-wrap">
            <table className="lr-laps-table">
              <thead>
                <tr>
                  <th className="lr-col-pos">#</th>
                  <th>LAP</th>
                  <th className="lr-col-delta">Δ BEST</th>
                </tr>
              </thead>
              <tbody>
                {race.lapsCompleted.length === 0 ? (
                  <tr className="lr-empty-row">
                    <td colSpan={3}>NO LAPS YET</td>
                  </tr>
                ) : (
                  race.lapsCompleted
                    .slice()
                    .reverse()
                    .map((lap: LapRecord) => {
                      const isBest = lap.lap === race.bestLapNum;
                      const delta = race.bestLapMs !== null ? lap.ms - race.bestLapMs : 0;
                      return (
                        <tr key={lap.lap} className={isBest ? "lr-best-row-hl" : ""}>
                          <td className="lr-col-pos">{String(lap.lap).padStart(2, "0")}</td>
                          <td>
                            <div className="lr-lap-time-line">{formatMs(lap.ms)}</div>
                            <div className="lr-lap-sectors">
                              {lap.sectors.map((_, i) => {
                                const cls =
                                  classifySector(
                                    race,
                                    lap.lap,
                                    i + 1,
                                    lap.sectors[i],
                                    lap.sectorWasPB[i] ?? false,
                                    lookupDbBestSectorMs(i + 1),
                                  ) ?? "pending";
                                return <span key={i} className={`lr-sp lr-${cls}`} />;
                              })}
                            </div>
                          </td>
                          <td className="lr-col-delta">
                            {isBest ? "BEST" : formatDelta(delta)}
                          </td>
                        </tr>
                      );
                    })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="lr-stream-section">
          <div className="lr-section-head">
            <span className="lr-section-title">GATE STREAM</span>
            <span className="lr-section-sub lr-mono">{race.stream.length}</span>
          </div>
          <ul className="lr-stream-list">
            {race.stream.slice(0, 8).map((ev, idx) => {
              if (ev.kind === "sector") {
                const cls = ev.detail === "sector-overall" ? "sector-overall" : "sector-slow";
                const toG = (ev.sectorIdx % nGates) + 1;
                return (
                  <li key={idx} className={`lr-stream-item lr-${cls}`}>
                    <span className="lr-gtag">
                      S{ev.sectorIdx} · G{ev.sectorIdx}→G{toG}
                    </span>
                    <span className="lr-gtime">{formatSector(ev.ms)} s</span>
                    <span className="lr-gdelta">
                      {ev.delta === null ? "NEW BEST" : formatDelta(ev.delta)}
                    </span>
                  </li>
                );
              }
              if (ev.kind === "lap-done") {
                return (
                  <li key={idx} className="lr-stream-item lr-sector-overall">
                    <span className="lr-gtag">LAP {ev.lap} COMPLETE</span>
                    <span className="lr-gtime">{formatMs(ev.ms)}</span>
                    <span className="lr-gdelta">
                      {ev.ms === race.bestLapMs ? "NEW BEST LAP" : ""}
                    </span>
                  </li>
                );
              }
              if (ev.kind === "lap-start") {
                return (
                  <li key={idx} className="lr-stream-item">
                    <span className="lr-gtag">LAP {ev.lap} START</span>
                    <span className="lr-gtime">G{ev.gate}</span>
                    <span className="lr-gdelta">t={(ev.t / 1000).toFixed(2)}s</span>
                  </li>
                );
              }
              if (ev.kind === "warmup") {
                return (
                  <li key={idx} className="lr-stream-item">
                    <span className="lr-gtag">WARMUP · G{ev.gate}</span>
                    <span className="lr-gtime">t={(ev.t / 1000).toFixed(2)}s</span>
                    <span className="lr-gdelta">pre-lap</span>
                  </li>
                );
              }
              return (
                <li key={idx} className="lr-stream-item lr-sector-slow">
                  <span className="lr-gtag">MISSED · got G{ev.gate}</span>
                  <span className="lr-gtime">exp G{ev.expected}</span>
                  <span className="lr-gdelta">skip</span>
                </li>
              );
            })}
          </ul>
        </div>
      </aside>
    </div>
  );
}
